import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Tv, Sparkles, Check, X } from 'lucide-react';
import './CardTV.css';

const TextRender = ({ htmlContent }) => {
  if (!htmlContent) return null;
  return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
};

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: 'easeOut',
    },
  },
};

const modalBackdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.28,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    y: 20,
    scale: 0.98,
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
    },
  },
};

const CardTV = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchPlans = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/planoTvs`
        );

        if (isMounted) {
          setPlans(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Erro ao buscar os planos de TV:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPlans();

    return () => {
      isMounted = false;
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? 'hidden' : 'unset';
  }, [isModalOpen]);

  const openModal = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPlan(null);
  };

  const getPlanTag = (planName = '') => {
    const normalized = planName.toUpperCase();

    if (normalized.includes('PREMIUM')) {
      return 'Mais completo';
    }

    if (normalized.includes('FAMILIA')) {
      return 'Ideal para casa';
    }

    if (normalized.includes('LIVRE')) {
      return 'Entrada';
    }

    return 'Streaming TV';
  };

  const buildFeatures = (plan) => {
    const features = [];

    if (plan.channels) {
      features.push(plan.channels);
    }

    if (plan.extraChannels) {
      features.push(plan.extraChannels);
    }

    if (plan.description) {
      features.push(plan.description);
    }

    return features.slice(0, 3);
  };

  return (
    <section className="tv-section">
      <div className="tv-section-header">
        <div className="tv-section-title-wrap">
          <div className="tv-section-icon-box">
            <Tv className="tv-section-main-icon" />
          </div>

          <div>
            <span className="tv-section-kicker">Gigabyte Play</span>
            <h2 className="tv-section-title">Planos Streaming de TV</h2>
            <p className="tv-section-subtitle">
              Escolha o plano ideal para sua casa e veja todos os detalhes antes
              de contratar.
            </p>
          </div>
        </div>

        <div className="tv-section-line" />
      </div>

      {loading ? (
        <div className="tv-state-box">
          <p>Carregando planos...</p>
        </div>
      ) : plans.length === 0 ? (
        <div className="tv-state-box">
          <p>Nenhum plano disponível no momento.</p>
        </div>
      ) : (
        <motion.div
  className="tv-cards-grid"
  variants={containerVariants}
  initial="hidden"
  animate="visible"
>
  {plans.map((plan) => {
    const isPremium = plan.name?.toUpperCase().includes('PREMIUM');
    const normalizedName = (plan.name || '').toUpperCase();

    const getQuickBenefits = () => {
      const items = [];

      if (plan.channels) items.push(plan.channels);
      if (plan.extraChannels) items.push(plan.extraChannels);

      if (items.length < 2) {
        if (normalizedName.includes('LIVRE')) {
          items.push('Ideal para quem busca canais abertos');
        } else if (normalizedName.includes('FAMILIA')) {
          items.push('Mais variedade para toda a família');
        } else if (normalizedName.includes('PREMIUM')) {
          items.push('Pacote mais completo da categoria');
        }
      }

      return items.slice(0, 2);
    };

    const quickBenefits = getQuickBenefits();

    const getShortLabel = () => {
      if (normalizedName.includes('LIVRE')) return 'Canais abertos e praticidade';
      if (normalizedName.includes('FAMILIA')) return 'Equilíbrio entre variedade e custo';
      if (normalizedName.includes('PREMIUM')) return 'Mais canais e melhor experiência';
      return 'Confira os detalhes do plano';
    };

    return (
      <motion.article
        key={plan.id}
        className={`tv-modern-card ${isPremium ? 'is-featured' : ''}`}
        variants={cardVariants}
        whileHover={{ y: -8 }}
      >
        {isPremium && (
          <div className="tv-card-highlight">
            <Sparkles size={16} />
            <span>Plano em destaque</span>
          </div>
        )}

        <div className="tv-card-top">
          <div className="tv-card-badge">
            <Tv size={16} />
            <span>{getPlanTag(plan.name)}</span>
          </div>

          <h3 className="tv-card-title">{plan.name}</h3>

          <p className="tv-card-short-text">{getShortLabel()}</p>
        </div>

        <div className="tv-card-features compact">
          {quickBenefits.map((item, index) => (
            <div key={index} className="tv-card-feature-item">
              <Check size={16} />
              <span>{item}</span>
            </div>
          ))}
        </div>

        <div className="tv-card-bottom">
          <div className="tv-card-price-box">
            <span className="tv-card-price-label">A partir de</span>
            <strong className="tv-card-price-value">
              R$ {plan.price},00
            </strong>
          </div>

          <button
            type="button"
            className="tv-card-button"
            onClick={() => openModal(plan)}
          >
            Ver detalhes
            <span className="tv-card-button-arrow">→</span>
          </button>
        </div>
      </motion.article>
    );
  })}
</motion.div>
      )}

      <AnimatePresence>
        {isModalOpen && selectedPlan && (
          <motion.div
            className="tv-modal-overlay"
            variants={modalBackdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={closeModal}
          >
            <motion.div
              className="tv-modal-shell"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="tv-modal-close"
                onClick={closeModal}
                aria-label="Fechar modal"
              >
                <X size={20} />
              </button>

              <div className="tv-modal-header">
                <div className="tv-modal-badge">
                  <Tv size={16} />
                  <span>{getPlanTag(selectedPlan.name)}</span>
                </div>

                <h3 className="tv-modal-title">{selectedPlan.name}</h3>

                <p className="tv-modal-subtitle">
                  Veja a descrição completa, condições e imagem ilustrativa do
                  plano.
                </p>

                <div className="tv-modal-line" />
              </div>

              <div className="tv-modal-content">
                <div className="tv-modal-info">
                  <h4 className="tv-modal-plan-name">{selectedPlan.name}</h4>

                  {selectedPlan.description && (
                    <p className="tv-modal-description">
                      {selectedPlan.description}
                    </p>
                  )}

                  <div className="tv-modal-richtext">
                    <TextRender htmlContent={selectedPlan.text} />
                  </div>

                  <div className="tv-modal-price-box">
                    <span className="tv-modal-price-label">A partir de</span>
                    <strong className="tv-modal-price-value">
                      R$ {selectedPlan.price},00
                    </strong>
                  </div>
                </div>

                <div className="tv-modal-media">
                  <div className="tv-modal-image-frame">
                    <img
                      src={`${process.env.REACT_APP_API_IMG}/assets/planoTvs/${selectedPlan.foto}`}
                      alt={selectedPlan.name}
                      className="tv-modal-image"
                    />
                  </div>
                </div>
              </div>

              <div className="tv-modal-footer">
                <p className="tv-modal-footer-text">
                  Baixe nosso aplicativo Gigabyte Play e comece a aproveitar,
                  mesmo que você não seja assinante da nossa internet.
                </p>

                <button
                  type="button"
                  className="tv-modal-footer-button"
                  onClick={closeModal}
                >
                  Fechar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default CardTV;